import { PricePeriodDefinition, PricingPlan, UpsellProduct } from "@base/types";

export function modifyUpsellProducts(
  upsellProducts: UpsellProduct[],
  modifiers: { currency?: string },
): UpsellProduct[] {
  return upsellProducts.map((it) => {
    const currency = modifiers.currency ?? it.currency;

    return {
      ...it,
      currency,
    };
  });
}

export function modifyPricingPlans(
  plans: PricingPlan[],
  modifiers: {
    promotionID?: string | null;
    currency?: string;
    priceModifierFactor?: number;
    minorUnitAmountRounding?: "down";
  },
): PricingPlan[] {
  return plans.map((p) => {
    const plan = calculateAmount(
      p,
      modifiers.promotionID || null,
      modifiers.priceModifierFactor || null,
      modifiers.minorUnitAmountRounding,
    );
    const currency = modifiers.currency ?? p.currency;

    const amountDaily = plan.trial.amount / getDayCount(plan.trial);
    const amountDailyBefore = plan.trial.amountBefore / getDayCount(plan.trial);
    const discount = (1 - plan.trial.amount / plan.trial.amountBefore) * 100;

    return {
      ...plan,
      discount,
      trial: {
        ...plan.trial,
        amountDaily,
        amountDailyBefore,
      },
      currency,
    };
  });
}

function calculateAmount(
  plan: PricingPlan,
  promotionID: string | null,
  priceModifierFactor: number | null,
  minorUnitAmountRounding?: "down",
): PricingPlan {
  priceModifierFactor = priceModifierFactor || 1;

  let trialOriginalAmount = plan.trial.originalAmount * priceModifierFactor;
  if (minorUnitAmountRounding === "down") {
    trialOriginalAmount = Math.floor(trialOriginalAmount / 100) * 100;
  }

  let trialAmountBefore = plan.trial.amountBefore * priceModifierFactor;
  if (minorUnitAmountRounding === "down") {
    trialAmountBefore = Math.floor(trialAmountBefore / 100) * 100;
  }

  let subscriptionOriginalAmount = plan.subscription.originalAmount * priceModifierFactor;
  if (minorUnitAmountRounding === "down") {
    subscriptionOriginalAmount = Math.floor(subscriptionOriginalAmount / 100) * 100;
  }

  const promotion = promotionID ? plan.promotions?.[promotionID] : undefined;

  let newAmount = trialOriginalAmount;
  if (promotion) {
    newAmount = trialOriginalAmount - promotion.trial.amountOff;
  }

  return {
    ...plan,
    trial: {
      ...plan.trial,
      amount: newAmount,
      amountBefore: trialAmountBefore,
      originalAmount: trialOriginalAmount,
      // only handles 1 offer for now, but data type is expandable for consistent tracking of future multiple offers
      appliedOfferIDs: promotion ? [promotion.id] : [],
    },
    // no offer on subscriptions for now, just keeping consistent typings
    subscription: {
      ...plan.subscription,
      amount: subscriptionOriginalAmount,
      originalAmount: subscriptionOriginalAmount,
    },
  };
}

export function getDayCount(price: PricePeriodDefinition): number {
  const { interval, intervalCount } = price;
  switch (interval) {
    case "day":
      return 1 * intervalCount;
    case "week": {
      // 1 month
      if (intervalCount === 4) {
        return 30;
      }

      // 3 months
      if (intervalCount === 12) {
        return 90;
      }

      return 7 * intervalCount;
    }
    case "month":
      return 30 * intervalCount;
    case "year":
      return 365 * intervalCount;
    default:
      return 0;
  }
}

/** "week", "month", "3 months" — reads naturally after "every" / "first". */
export function formatPeriod({ interval, intervalCount }: PricePeriodDefinition) {
  return intervalCount === 1 ? interval : `${intervalCount} ${interval}s`;
}
